// Services Overview Page - All yoga classes and treatments
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Clock, MapPin, Check } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

// Services Page Schema
const ServicesSchema = () => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Yoga & Wellness Services by Annelie Lily",
      "description": "Complete list of yoga classes, holistic treatments, women's circles and retreats offered by Annelie Lily in Frome, Somerset.",
      "url": "https://annelielily.com/services",
      "numberOfItems": 4,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Service",
            "name": "Yoga Classes",
            "url": "https://annelielily.com/yoga-classes"
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Service",
            "name": "Healing & Treatments",
            "url": "https://annelielily.com/treatments"
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Service",
            "name": "Women's Circles",
            "url": "https://annelielily.com/womens-circles"
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "Service",
            "name": "Retreats",
            "url": "https://annelielily.com/retreats"
          }
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'services-schema';
    document.head.appendChild(script);
    
    return () => {
      const el = document.getElementById('services-schema');
      if (el) el.remove();
    };
  }, []);
  
  return null;
};

const ServicesPage = () => {
  usePageMeta({
    title: "Yoga Classes & Treatments in Frome | All Services",
    description: "Complete list of yoga classes, holistic treatments, women's circles and retreats in Frome, Somerset. Hatha, Yin, Aerial yoga, Reiki, massage and more with",
    canonical: "https://annelielily.com/services"
  });

  const yogaClasses = [
    {
      title: "Hatha Yoga",
      description: "The traditional practice most people picture when they think of yoga. Standing poses, seated stretches, twists, gentle inversions, and breathing work. Classes build steadily, always ending with relaxation. Good for anyone wanting a balanced practice that works the whole body.",
      link: "/yoga-classes/hatha-yoga",
      duration: "75-90 min",
      level: "All levels"
    },
    {
      title: "Yin Yoga", 
      description: "Completely different pace. We hold poses for 3-5 minutes, using props for support while gravity does the work. Targets the deeper connective tissue rather than muscles. If you sit at a desk all day, carry tension in your hips, or struggle to switch off, Yin is worth trying.",
      link: "/yoga-classes/yin-yoga",
      duration: "60-75 min",
      level: "All levels"
    },
    {
      title: "Aerial Yoga",
      description: "Yoga with a twist — using a fabric hammock suspended from the ceiling. Inversions become accessible, stretches go deeper, and there's something genuinely playful about practicing in the air. Private 1-to-1 sessions only.",
      link: "/yoga-classes/aerial-yoga",
      duration: "60 min",
      level: "1-to-1 only"
    },
    {
      title: "Private Sessions",
      description: "One-to-one yoga tailored entirely to you. Some people want this because they're nervous about group classes. Others have specific things to work on — an injury, pregnancy, a health condition. We meet, talk about what you need, and I build sessions around that.",
      link: "/yoga-classes/private-yoga",
      duration: "60-90 min",
      level: "Personalised"
    },
    {
      title: "Children & Family Yoga",
      description: "Playful, engaging and supportive yoga for children, teenagers and families. Through movement, breath and imagination, young people learn simple tools that support calm, focus and wellbeing.",
      link: "/yoga-classes/children-family-yoga",
      duration: "Varies",
      level: "All ages"
    },
    {
      title: "SEN Yoga",
      description: "Specialist yoga for children and young people with special educational needs. Gentle, adaptive classes carefully tailored to each individual, creating a safe and inclusive environment.",
      link: "/yoga-classes/sen-yoga",
      duration: "Varies",
      level: "Specialist"
    },
    {
      title: "Meditation",
      description: "A simple yet powerful way to quiet the mind, reduce anxiety and reconnect with inner calm. Classes and private sessions available for both beginners and those wanting to deepen their practice.",
      link: "/yoga-classes/meditation",
      duration: "30-60 min",
      level: "All levels"
    },
  ];

  const treatments = [
    {
      title: "Healing & Treatments",
      description: "Massage, energy healing and intuitive guidance to support deep rest, release and transformation. A space where your body can soften, your mind can settle, and you can begin to reconnect with yourself.",
      link: "/treatments",
      includes: ["Massage", "Reiki", "Chakra Healing", "Crystal Work", "Guidance Cards"]
    },
  ];

  const circles = [
    {
      title: "Women's Circles",
      description: "A space to step out of the busyness of everyday life and come back to yourself. A space to slow down, to be heard, and to connect with others in a way that often feels missing in modern life.",
      link: "/womens-circles",
      frequency: "Monthly"
    },
  ];

  const retreats = [
    {
      title: "Day Retreats",
      description: "Full days at The Station in Frome. Yoga in the morning, sauna and cold plunge at lunchtime, more yoga in the afternoon, sound bath to finish. Lunch included. A proper reset without having to travel anywhere or take time off work.",
      link: "/retreats",
      dates: "2026 dates coming soon"
    },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <ServicesSchema />
      <Navigation />
      <PageBreadcrumb currentPage="All Services" currentSlug="services" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              What I Offer
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Classes & Treatments
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto">
              Everything happens at my space in Frome, Somerset. Small groups, personal attention, 
              and a genuine focus on helping you feel better — not just during class, but in daily life.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm text-neutral-600">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full">
                <Users className="w-4 h-4 text-gold" />
                Max 8 per class
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-gold" />
                BWY certified since 2013
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-gold" />
                Frome, Somerset
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Yoga Classes */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <h2 className="font-heading text-3xl text-neutral-800 mb-2">Yoga Classes</h2>
            <p className="text-neutral-600">Regular group sessions and private tuition</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {yogaClasses.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.link}
                  className="block bg-white p-6 rounded-xl border border-gold/10 hover:border-gold/30 hover:shadow-md transition-all h-full group"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-gold/10 text-gold px-2 py-1 rounded-full">{item.level}</span>
                    <span className="text-xs text-neutral-500">{item.duration}</span>
                  </div>
                  <h3 className="font-heading text-xl text-neutral-800 mb-3 group-hover:text-gold transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-4">{item.description}</p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments & Circles */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Treatments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">Healing Treatments</h2>
              {treatments.map((item) => (
                <Link
                  key={item.title}
                  to={item.link}
                  className="block bg-white p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
                >
                  <h3 className="font-heading text-xl text-neutral-800 mb-3 group-hover:text-gold transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-4">{item.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.includes.map((inc) => (
                      <span key={inc} className="text-xs bg-gold/10 text-gold px-2 py-1 rounded-full">
                        {inc}
                      </span>
                    ))}
                  </div>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    View treatments <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </motion.div>

            {/* Circles */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">Women's Circles</h2>
              {circles.map((item) => (
                <Link
                  key={item.title}
                  to={item.link}
                  className="block bg-white p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-gold/10 text-gold px-2 py-1 rounded-full">{item.frequency}</span>
                  </div>
                  <h3 className="font-heading text-xl text-neutral-800 mb-3 group-hover:text-gold transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-4">{item.description}</p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    About circles <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Retreats */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="font-heading text-2xl text-neutral-800 mb-2">Day Retreats</h2>
            <p className="text-neutral-600">Full day experiences at The Station, Frome</p>
          </motion.div>

          {retreats.map((item) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link
                to={item.link}
                className="block bg-white p-8 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
              >
                <div className="md:flex md:items-center md:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs bg-gold/10 text-gold px-2 py-1 rounded-full">{item.dates}</span>
                      <span className="text-xs text-neutral-500">£85 per person</span>
                    </div>
                    <h3 className="font-heading text-xl text-neutral-800 mb-3 group-hover:text-gold transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-neutral-600 text-sm leading-relaxed mb-4 md:mb-0">{item.description}</p>
                  </div>
                  <div className="md:ml-8">
                    <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      View & book <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Not Sure? */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Not Sure What's Right for You?
            </h2>
            <p className="text-neutral-600 mb-6 leading-relaxed">
              If you're new to yoga, returning after a break, or dealing with a particular health concern, 
              just get in touch. I'm happy to chat through what might work best — no pressure to book anything.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">
            Based in Frome, serving the surrounding area:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link
                key={area.name}
                to={area.link}
                className="text-sm text-gold hover:underline underline-offset-4"
              >
                {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
